/**
 * Find the point on a splat surface under a screen position.
 *
 * Splats write no depth, so there is nothing to read back from the GPU.
 * Instead we test the CPU copy of the splat centres (resource.centers, which
 * the engine keeps for sorting) against a thin cone around the pick ray.
 *
 * The hit is the median of the NEAREST few candidates along the ray, not the
 * nearest one: a stray floater or two in front of a surface cannot win. Large
 * flat splats (floors, walls) leave few centres per pixel, so when the cone
 * holds too few candidates it widens step by step. Past that (a floor made of
 * a handful of huge splats, seen from far: no centre near the ray at all) the
 * nearest dozen centres in a wide cone get a plane fitted through them and
 * the ray is intersected with that plane. (A "densest cluster" rule was tried
 * first: at a grazing angle a floor's centres spread out along the ray and
 * never cluster, so the ray went straight through the floor to the wall.)
 *
 * The engine's GPU Picker was tried too: with this viewer's own render
 * pipeline it returned points tens of metres off, and on WebGPU its readback
 * never resolved.
 *
 * LOD octree assets (lod-meta.json) have no single centres array; there we
 * test every chunk the octree currently has loaded (octree.fileResources), all
 * in the same local space. Overlapping LOD levels only add candidates.
 *
 * One pick costs one pass over the centres (~10 ms for 2M splats), so this is
 * for clicks and throttled hover, never every frame.
 *
 * `depth` limits the candidates to a stretch of the ray (world distance from
 * the near-plane point). The measure tool passes a window around the
 * collision-voxel hit: the voxels say WHICH surface (they cannot see through a
 * white wall), the splat centres say exactly where its visible skin is.
 */

import * as pc from 'playcanvas';

/** Candidates taken from the front of the ray; the hit is their median. */
const NEAREST = 5;
/** Cone radii tried in turn, in screen pixels, until NEAREST candidates fit. */
const CONE_STEPS_PX = [4, 8, 16];
/** Last resort for sparse surfaces: plane through the nearest PLANE_POINTS
 *  centres inside this cone. */
const PLANE_CONE_PX = 48;
const PLANE_POINTS = 12;

const invWorld = new pc.Mat4();
const near = new pc.Vec3();
const far = new pc.Vec3();
const lo = new pc.Vec3();
const lf = new pc.Vec3();
const rd = new pc.Vec3();
const local = new pc.Vec3();
const scale = new pc.Vec3();

/**
 * World-space surface point under (sx, sy), CSS pixels relative to the canvas,
 * or null when the ray hits no splat.
 */
export function pickSplat(
    cameraEntity: pc.Entity,
    splats: Iterable<pc.Entity>,
    sx: number,
    sy: number,
    canvasWidth: number,
    canvasHeight: number,
    depth?: [number, number]
): pc.Vec3 | null {
    const cam = cameraEntity.camera;
    // The fov is on the width when the scene asked for a horizontal fov.
    const fovAxisPx = cam?.horizontalFov ? canvasWidth : canvasHeight;
    if (!cam || fovAxisPx <= 0) return null;
    cam.screenToWorld(sx, sy, cam.nearClip, near);
    cam.screenToWorld(sx, sy, cam.farClip, far);
    // tan of the cone half-angle per screen pixel. A uniform scale on the
    // splat leaves angles alone, so the same value works in local space.
    const tanPerPx = (2 * Math.tan((cam.fov * Math.PI) / 360)) / fovAxisPx;
    const tanSteps = CONE_STEPS_PX.map(px => px * tanPerPx);
    const tanPlane = PLANE_CONE_PX * tanPerPx;
    const camPos = cameraEntity.getPosition();

    let best: pc.Vec3 | null = null;
    let bestDist = Infinity;

    for (const entity of splats) {
        if (!entity.enabled) continue;
        const arrays = centerArrays(entity);
        if (arrays.length === 0) continue;

        const world = entity.getWorldTransform();
        invWorld.copy(world).invert();
        invWorld.transformPoint(near, lo);
        invWorld.transformPoint(far, lf);
        rd.sub2(lf, lo).normalize();
        // world distance -> local distance (splats are scaled uniformly)
        const k = 1 / (world.getScale(scale).x || 1);
        const tMin = depth ? depth[0] * k : 0;
        const tMax = depth ? depth[1] * k : Infinity;

        const hits: Candidate[] = [];
        for (const c of arrays) collect(c, lo.x, lo.y, lo.z, rd.x, rd.y, rd.z, tanPlane, tMin, tMax, hits);
        const t = surfaceDistance(hits, tanSteps, lo, rd);
        if (t === null) continue;

        local.copy(rd).mulScalar(t).add(lo);
        const p = new pc.Vec3();
        world.transformPoint(local, p);
        const d = p.distance(camPos);
        if (d < bestDist) {
            bestDist = d;
            best = p;
        }
    }
    return best;
}

/** A centre inside the widest cone: its distance along the ray, and how far
 *  off the ray it sits (tan of its angle). */
interface Candidate { t: number; tan: number; x: number; y: number; z: number }

type CentersHolder = { centers?: Float32Array | null };
type OctreeHolder = {
    octree?: { fileResources?: Map<number, CentersHolder>; environmentResource?: CentersHolder | null } | null;
};

/** Every CPU centres array behind a splat entity, in its local space. */
function centerArrays(entity: pc.Entity): Float32Array[] {
    const res = entity.gsplat?.resource as (CentersHolder & OctreeHolder) | null | undefined;
    if (!res) return [];
    const out: Float32Array[] = [];
    const add = (h: CentersHolder | null | undefined) => {
        const c = h?.centers;
        if (c && c.length >= 3) out.push(c);
    };
    const octree = res.octree;
    if (octree) {
        octree.fileResources?.forEach(add);
        add(octree.environmentResource);
    } else {
        add(res);
    }
    return out;
}

/** Push every centre inside the cone of half-angle atan(maxTan), between
 *  tMin and tMax along the ray, onto `out`. */
function collect(
    c: Float32Array,
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    maxTan: number,
    tMin: number, tMax: number,
    out: Candidate[]
): void {
    const max2 = maxTan * maxTan;
    for (let i = 0, n = c.length - 2; i < n; i += 3) {
        const vx = c[i] - ox, vy = c[i + 1] - oy, vz = c[i + 2] - oz;
        const t = vx * dx + vy * dy + vz * dz;
        if (t <= 0 || t < tMin || t > tMax) continue;
        const perp2 = Math.max(0, vx * vx + vy * vy + vz * vz - t * t);
        const tt = t * t;
        if (perp2 <= tt * max2) out.push({ t, tan: Math.sqrt(perp2 / tt), x: c[i], y: c[i + 1], z: c[i + 2] });
    }
}

/** Ray distance to the surface: median of the nearest NEAREST candidates in
 *  the narrowest cone that holds that many; failing that, the plane through
 *  the nearest candidates of the wide cone; null when the cone is empty. */
function surfaceDistance(hits: Candidate[], tanSteps: number[], o: pc.Vec3, d: pc.Vec3): number | null {
    if (hits.length === 0) return null;
    hits.sort((a, b) => a.t - b.t);
    for (const tan of tanSteps) {
        const front: number[] = [];
        for (const h of hits) {
            if (h.tan > tan) continue;
            front.push(h.t);
            if (front.length === NEAREST) return front[NEAREST >> 1];
        }
    }
    const pts = hits.slice(0, PLANE_POINTS);
    const fallback = pts[pts.length >> 1].t;
    if (pts.length < 4) return fallback;
    const t = rayPlaneFit(pts, o, d);
    // A wild intersection (grazing plane, mixed surfaces) is worse than the
    // plain median: keep it only near the depths the points span.
    const lo = pts[0].t, hi = pts[pts.length - 1].t;
    return t !== null && t >= lo * 0.5 && t <= hi * 1.5 ? t : fallback;
}

/** Least-squares plane through the points, intersected with the ray. */
function rayPlaneFit(pts: Candidate[], o: pc.Vec3, d: pc.Vec3): number | null {
    let cx = 0, cy = 0, cz = 0;
    for (const p of pts) { cx += p.x; cy += p.y; cz += p.z; }
    cx /= pts.length; cy /= pts.length; cz /= pts.length;
    let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
    for (const p of pts) {
        const x = p.x - cx, y = p.y - cy, z = p.z - cz;
        xx += x * x; xy += x * y; xz += x * z; yy += y * y; yz += y * z; zz += z * z;
    }
    // Normal = the axis the points spread least along: solve with the
    // largest of the three 2x2 determinants for the best conditioning.
    const detX = yy * zz - yz * yz, detY = xx * zz - xz * xz, detZ = xx * yy - xy * xy;
    let nx: number, ny: number, nz: number;
    if (detX >= detY && detX >= detZ) {
        nx = detX; ny = xz * yz - xy * zz; nz = xy * yz - xz * yy;
    } else if (detY >= detZ) {
        nx = xz * yz - xy * zz; ny = detY; nz = xy * xz - yz * xx;
    } else {
        nx = xy * yz - xz * yy; ny = xy * xz - yz * xx; nz = detZ;
    }
    const len = Math.hypot(nx, ny, nz);
    if (len === 0) return null;
    const denom = (nx * d.x + ny * d.y + nz * d.z) / len;
    if (Math.abs(denom) < 1e-3) return null;
    return ((nx * (cx - o.x) + ny * (cy - o.y) + nz * (cz - o.z)) / len) / denom;
}
